import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { suggestReviewImprovements } from '@/features/assistant/api'
import { AssistantPanel } from '@/features/assistant/AssistantPanel'
import { useAuth } from '@/features/auth/store'
import {
  REVIEW_RATING_LABELS,
  REVIEW_STATUS_LABELS,
  SCORE_COMPONENTS,
  approveReview,
  deleteReview,
  formatPeriod,
  formatRatio,
  formatScore,
  getReview,
  missingScores,
  refreshMetrics,
  signOffReview,
  submitReview,
  updateReview,
  type Review,
} from '@/features/hr/api'
import { formatMoney } from '@/lib/money'
import { toast } from '@/lib/toast'

/** Statuses in which the content of a review may still be edited. Mirrors the API. */
const OPEN_STATUSES = new Set(['draft', 'submitted'])

const fail = (error: unknown) =>
  toast.error(
    error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
  )

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium tabular-nums">{value}</span>
    </div>
  )
}

/**
 * The four component scores and what each one is worth.
 *
 * The weight is on the screen because the overall score is weighted rather than averaged,
 * and a reader who assumes a mean will think the arithmetic is wrong. Which scores were
 * measured and which were typed is on the screen for a harder reason: they carry different
 * authority in the conversation this review exists for.
 */
function Components({ review }: { review: Review }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
          <th className="py-2 font-medium">Component</th>
          <th className="py-2 font-medium">Source</th>
          <th className="py-2 text-right font-medium">Weight</th>
          <th className="py-2 text-right font-medium">Score</th>
        </tr>
      </thead>
      <tbody>
        {SCORE_COMPONENTS.map((component) => (
          <tr key={component.key} className="border-b border-slate-100 dark:border-slate-800/60">
            <td className="py-2 font-medium">{component.label}</td>
            <td className="py-2 text-slate-500 dark:text-slate-400">
              {component.key === 'attendance_score'
                ? 'Measured from attendance'
                : component.computed
                  ? 'Measured from orders'
                  : 'Entered by the manager'}
            </td>
            <td className="py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
              {Math.round(component.weight * 100)}%
            </td>
            <td className="py-2 text-right font-medium tabular-nums">
              {formatScore(review[component.key])}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Where the two computed scores came from: the rows, the filter and the arithmetic. */
function Provenance({ review }: { review: Review }) {
  const metrics = review.metrics

  if (!metrics) {
    return (
      <CardBody>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Nothing has been pulled from the orders yet. Until it is, the sales and KPI halves of this
          review are whatever was typed in.
        </p>
      </CardBody>
    )
  }

  return (
    <CardBody className="grid gap-6 md:grid-cols-3">
      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Sales
        </h3>
        {metrics.sales.unavailable ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{metrics.sales.unavailable}</p>
        ) : (
          <>
            <Row label="Revenue earned" value={formatMoney(metrics.sales.revenue)} />
            <Row
              label="Target for the period"
              value={formatMoney(metrics.sales.period_target ?? '0')}
            />
            <Row label="Achievement" value={formatRatio(metrics.sales.achievement)} />
          </>
        )}
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          KPI
        </h3>
        {metrics.kpi.unavailable ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{metrics.kpi.unavailable}</p>
        ) : (
          <>
            <Row label="Orders committed" value={String(metrics.kpi.orders_committed)} />
            <Row label="Reached fulfilment" value={String(metrics.kpi.orders_fulfilled)} />
            <Row label="Fulfilment rate" value={formatRatio(metrics.kpi.fulfilment_rate)} />
          </>
        )}
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Attendance
        </h3>
        {metrics.attendance.unavailable ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {metrics.attendance.unavailable}
          </p>
        ) : (
          <>
            <Row label="Days expected" value={String(metrics.attendance.expected_days)} />
            <Row label="Days present" value={String(metrics.attendance.present_days)} />
            <Row label="Rate" value={formatRatio(metrics.attendance.rate)} />
          </>
        )}
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {metrics.source.attendance_table === 'attendance'
            ? 'Measured from attendance records.'
            : 'No attendance marked in this period -- measured from field routes and visit check-ins instead.'}{' '}
          <Link
            to={`/hr/attendance?employee_id=${review.employee_id}&date_from=${review.review_period_start}&date_to=${review.review_period_end}`}
            className="text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
          >
            View the days behind this
          </Link>
          .
        </p>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 md:col-span-3">
        Sales and KPI read from {metrics.source.table} for {metrics.source.order_date_from} to{' '}
        {metrics.source.order_date_to}, counting {metrics.source.counted_statuses.join(', ')}.
        Computed {new Date(metrics.computed_at).toLocaleString()}.
      </p>
    </CardBody>
  )
}

/**
 * One review: what it scored, where the numbers came from, and what happens next.
 *
 * The screen is built around the thing that makes this module worth having — a score with
 * its provenance attached. Six months later, when the rep disputes a 2.0 and the orders
 * behind it have moved on to other statuses, the stored figures are what makes the number
 * defensible, so they are shown here rather than kept in a JSON column nobody opens.
 */
const PRIORITY_STYLE: Record<'high' | 'medium' | 'low', string> = {
  high: 'bg-danger/10 text-danger',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-400',
  low: 'bg-success/10 text-success',
}

export function PerformanceReviewDetail() {
  const { reviewId = '' } = useParams()
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const {
    data: review,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['review', reviewId],
    queryFn: () => getReview(reviewId),
    retry: false,
  })

  const [attendance, setAttendance] = useState('')
  const [managerScore, setManagerScore] = useState('')
  const [managerNote, setManagerNote] = useState('')
  const [selfNote, setSelfNote] = useState('')

  // Seeded from the server copy, not bound to it: a half-typed score must survive the
  // refetch that follows pulling the metrics in.
  useEffect(() => {
    if (!review) return
    setAttendance(review.attendance_score ?? '')
    setManagerScore(review.manager_score ?? '')
    setManagerNote(review.manager_assessment ?? '')
    setSelfNote(review.self_assessment ?? '')
  }, [review?.id, review?.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps

  const settled = (message: string) => (updated: Review) => {
    queryClient.setQueryData(['review', reviewId], updated)
    void queryClient.invalidateQueries({ queryKey: ['reviews'] })
    toast.success(message)
  }

  const save = useMutation({
    mutationFn: () =>
      updateReview(reviewId, {
        attendance_score: attendance || null,
        manager_score: managerScore || null,
        manager_assessment: managerNote || null,
        self_assessment: selfNote || null,
      }),
    onSuccess: settled('Saved.'),
    onError: fail,
  })
  const pull = useMutation({
    mutationFn: () => refreshMetrics(reviewId),
    onSuccess: settled('Pulled sales, KPI and attendance figures.'),
    onError: fail,
  })
  const submit = useMutation({
    mutationFn: () => submitReview(reviewId),
    onSuccess: settled('Submitted for approval.'),
    onError: fail,
  })
  const approve = useMutation({
    mutationFn: () => approveReview(reviewId),
    onSuccess: settled('Approved.'),
    onError: fail,
  })
  const withdraw = useMutation({
    mutationFn: () => deleteReview(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reviews'] })
      toast.success('Withdrawn.')
      void navigate('/hr/reviews')
    },
    onError: fail,
  })
  const sign = useMutation({
    mutationFn: (side: 'employee' | 'manager') =>
      signOffReview(reviewId, side === 'employee' ? { as_employee: true } : { as_manager: true }),
    onSuccess: settled('Signed.'),
    onError: fail,
  })

  if (isLoading) return <Spinner />
  if (error || !review) {
    return (
      <Alert tone="error" title="Could not open this review">
        {error instanceof ApiError ? error.message : 'It may have been withdrawn.'}
      </Alert>
    )
  }

  const missing = missingScores(review)
  const editable = OPEN_STATUSES.has(review.status) && can('performance_reviews', 'update')

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/hr/reviews"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All reviews
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {review.employee_name ?? 'Performance review'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {review.employee_code} · {review.employee_position} ·{' '}
            {formatPeriod(review.review_period_start, review.review_period_end)} ·{' '}
            {REVIEW_STATUS_LABELS[review.status]}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-semibold tabular-nums">{formatScore(review.overall_score)}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {review.rating ? REVIEW_RATING_LABELS[review.rating] : 'Not yet scored'}
          </p>
        </div>
      </header>

      {missing.length > 0 && (
        <Alert tone="info" title="Insufficient data for evaluation period">
          Missing{' '}
          {missing
            .map((key) => SCORE_COMPONENTS.find((c) => c.key === key)?.label.toLowerCase())
            .join(', ')}
          . A score computed from the rest would flatter or punish the person by whatever happens to
          be filled in, so there is no overall score until all four are here.
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        {can('performance_reviews', 'update') && review.status === 'draft' && (
          <Button variant="secondary" onClick={() => pull.mutate()} loading={pull.isPending}>
            Pull figures
          </Button>
        )}
        {editable && review.status === 'draft' && (
          <Button onClick={() => submit.mutate()} loading={submit.isPending}>
            Submit for approval
          </Button>
        )}
        {can('performance_reviews', 'update') && review.status === 'submitted' && (
          <Button onClick={() => approve.mutate()} loading={approve.isPending}>
            Approve
          </Button>
        )}
        {/* Both sign-off buttons are offered to anyone who can read the review. Which
            side a caller may actually sign is the server's decision -- the employee's
            signature has to come from the employee -- and hiding the button would mean
            guessing at that rule from a screen that cannot see who the employee is. */}
        {review.status === 'approved' && !review.signoff_by_manager && (
          <Button
            variant="secondary"
            onClick={() => sign.mutate('manager')}
            loading={sign.isPending && sign.variables === 'manager'}
          >
            Sign as manager
          </Button>
        )}
        {review.status === 'approved' && !review.signoff_by_employee && (
          <Button
            variant="secondary"
            onClick={() => sign.mutate('employee')}
            loading={sign.isPending && sign.variables === 'employee'}
          >
            Sign as employee
          </Button>
        )}
        {OPEN_STATUSES.has(review.status) && can('performance_reviews', 'delete') && (
          <Button variant="ghost" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>
            Withdraw
          </Button>
        )}
      </div>

      {review.recommendations && review.recommendations.length > 0 && (
        <Card>
          <CardHeader
            title="Recommendations"
            description="Worked out from sales against target, attendance, KPIs and the manager's score."
          />
          <CardBody>
            <ul className="space-y-2">
              {review.recommendations.map((item) => (
                <li key={`${item.area}-${item.text}`} className="flex items-start gap-2 text-sm">
                  <span
                    className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLE[item.priority]}`}
                  >
                    {item.priority}
                  </span>
                  <span className="text-slate-700 dark:text-slate-300">{item.text}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {can('performance_reviews', 'update') && (
        <AssistantPanel
          title="Suggest improvements"
          description="Drafts improvement goals from this review's scores and figures."
          buttonLabel="Suggest"
          ask={() => suggestReviewImprovements(reviewId)}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="How the score was made"
            description="Weighted, not averaged: 30% sales, 30% KPI, 20% attendance, 20% the manager."
          />
          <CardBody>
            <Components review={review} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="The two a person types"
            description="Attendance is pre-filled above by Pull figures but can be overridden; a manager's judgement is not a number anything can compute."
          />
          <CardBody className="space-y-4">
            {!editable && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                This review is {REVIEW_STATUS_LABELS[review.status].toLowerCase()} and can no longer
                be edited.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Attendance" hint="0 to 5.">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    max={5}
                    step={0.5}
                    disabled={!editable}
                    value={attendance}
                    onChange={(event) => setAttendance(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Manager's score" hint="0 to 5.">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    max={5}
                    step={0.5}
                    disabled={!editable}
                    value={managerScore}
                    onChange={(event) => setManagerScore(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <Field label="Manager's assessment">
              {(props) => (
                <Input
                  {...props}
                  disabled={!editable}
                  value={managerNote}
                  onChange={(event) => setManagerNote(event.target.value)}
                  placeholder="What was said in the conversation"
                />
              )}
            </Field>
            <Field label="Self assessment">
              {(props) => (
                <Input
                  {...props}
                  disabled={!editable}
                  value={selfNote}
                  onChange={(event) => setSelfNote(event.target.value)}
                  placeholder="In the employee's own words"
                />
              )}
            </Field>
            {editable && (
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                Save
              </Button>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Where the measured scores came from"
          description="Stored on the review when the figures were pulled, so the number stays traceable after the orders move on."
        />
        <Provenance review={review} />
      </Card>

      {Boolean(review.goals?.length || review.development_plan?.length) && (
        <Card>
          <CardHeader title="Goals and development" />
          <CardBody className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Goals
              </h3>
              <ul className="space-y-2 text-sm">
                {(review.goals ?? []).map((goal) => (
                  <li key={goal.title}>
                    <span className="font-medium">{goal.title}</span>
                    {goal.detail && (
                      <span className="block text-slate-500 dark:text-slate-400">
                        {goal.detail}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Development plan
              </h3>
              <ul className="space-y-2 text-sm">
                {(review.development_plan ?? []).map((item) => (
                  <li key={item.title}>
                    <span className="font-medium">{item.title}</span>
                    {item.detail && (
                      <span className="block text-slate-500 dark:text-slate-400">
                        {item.detail}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </CardBody>
        </Card>
      )}

      {review.signoff_at && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Signed by both sides on {new Date(review.signoff_at).toLocaleDateString()}. The review is
          closed.
        </p>
      )}
    </div>
  )
}
